export interface CatalogImageRecord {
    id?: number;
    path?: string;
    thumb_s?: string;
    thumb_m?: string;
    thumb_r?: string;
}

export interface CatalogPagination {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
    from: number;
    to: number;
}

export interface BrandRecord {
    id: number;
    name: string;
    slug?: string | null;
    description?: string | null;
    top?: boolean | number | null;
    image_id?: number | null;
    sort?: number | null;
    active?: boolean | number | null;
    show_home?: boolean | number | null;
    images?: CatalogImageRecord | null;
    created_at?: string;
    updated_at?: string;
}

export interface CategoryRecord {
    id: number;
    name: string;
    label?: string | null;
    slug?: string | null;
    description?: string | null;
    parent_id?: number | null;
    top?: boolean | number | null;
    image_id?: number | null;
    sort?: number | null;
    active?: boolean | number | null;
    seo_id?: number | null;
    seo?: unknown;
    files?: CatalogImageRecord | null;
    images?: CatalogImageRecord[];
    children?: CategoryRecord[];
    created_at?: string;
    updated_at?: string;
    level?: number;
}

export interface ProductRecord {
    quantity: number | null | undefined;
    id: number;
    brand_id?: number | null;
    name: string;
    sku?: string | null;
    slug?: string | null;
    product_type?: string | null;
    qty?: number | null;
    price?: number | null;
    active?: boolean | number | null;
    description?: string | null;
    short_description?: string | null;
    main_description?: string | null;
    seo_id?: number | null;
    model_no?: string | null;
    item_weight?: number | null;
    length?: string | null;
    height?: string | null;
    width?: string | null;
    rate?: boolean | number | null;
    trending?: boolean | number | null;
    new?: boolean | number | null;
    voucher_code?: string | null;
    voucher_validity?: string | null;
    offer?: string | null;
    offer_price?: number | null;
    credit_price?: number | null;
    voucher_price?: number | null;
    offer_desc?: string | null;
    from_date?: string | null;
    to_date?: string | null;
    special_price?: number | null;
    special_price_start_date?: string | null;
    special_price_end_date?: string | null;
    video_url?: string | null;
    video_image_url?: string | null;
    youtube_code?: string | null;
    youtube_url?: string | null;
    '360_view'?: string | null;
    images?: CatalogImageRecord[];
    files?: CatalogImageRecord | null;
    brands?: BrandRecord | null;
    stores?: Array<{ id: number; name: string; }>;
    tags?: Array<{ id: number; name: string; }>;
    categories?: CategoryRecord[];
    related_products?: ProductRecord[];
    relatedProducts?: ProductRecord[];
    recommended_products?: ProductRecord[];
    recommendedProducts?: ProductRecord[];
    seo?: unknown;
    created_at?: string;
    updated_at?: string;
}

const asRecord = (value: unknown): Record<string, any> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    return value as Record<string, any>;
};

export const toBoolean = (value: unknown): boolean =>
    value === true || value === 1 || value === '1';

export const sanitizeSlug = (value: string): string => value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 100);

export const extractApiErrorMessage = (err: any, fallback: string): string => {
    const message = err?.response?.data?.messages || err?.response?.data?.message || err?.message;

    if (Array.isArray(message)) {
        return message.join(', ');
    }

    return typeof message === 'string' && message.trim() ? message : fallback;
};

const extractApiPayload = (response: any): unknown => {
    const data = response?.data;
    const record = asRecord(data);

    if (record && 'data' in record) {
        return record.data;
    }

    return data ?? response;
};

const extractItemsArray = <T>(payload: unknown): T[] => {
    if (Array.isArray(payload)) {
        return payload as T[];
    }

    const record = asRecord(payload);

    if (record && Array.isArray(record.data)) {
        return record.data as T[];
    }

    return [];
};

const extractPagination = (payload: unknown, itemCount: number): CatalogPagination | null => {
    const record = asRecord(payload);

    if (!record || !Array.isArray(record.data)) {
        return null;
    }

    const currentPage = Number(record.current_page);
    const lastPage = Number(record.last_page);
    const perPage = Number(record.per_page);
    const total = Number(record.total);
    const from = Number(record.from);
    const to = Number(record.to);

    if (!Number.isFinite(currentPage) || !Number.isFinite(lastPage)) {
        return null;
    }

    return {
        currentPage,
        lastPage,
        perPage: Number.isFinite(perPage) && perPage > 0 ? perPage : itemCount,
        total: Number.isFinite(total) && total >= 0 ? total : itemCount,
        from: Number.isFinite(from) ? from : itemCount > 0 ? 1 : 0,
        to: Number.isFinite(to) ? to : itemCount,
    };
};

export const extractBrandCollection = (response: any): { items: BrandRecord[]; pagination: CatalogPagination | null; } => {
    const payload = extractApiPayload(response);
    const items = extractItemsArray<BrandRecord>(payload);

    return {
        items,
        pagination: extractPagination(payload, items.length),
    };
};

export const extractCategoryTree = (response: any): CategoryRecord[] => {
    const payload = extractApiPayload(response);
    return extractItemsArray<CategoryRecord>(payload);
};

export const extractProductCollection = (response: any): { items: ProductRecord[]; pagination: CatalogPagination | null; } => {
    const payload = extractApiPayload(response);
    const items = extractItemsArray<ProductRecord>(payload);

    return {
        items,
        pagination: extractPagination(payload, items.length),
    };
};

export const extractProductList = (response: any): ProductRecord[] => {
    const payload = extractApiPayload(response);
    return extractItemsArray<ProductRecord>(payload);
};

export const extractProductDetail = (response: any): ProductRecord | null => {
    const payload = extractApiPayload(response);

    if (Array.isArray(payload)) {
        return payload[0] ?? null;
    }

    const record = asRecord(payload);
    if (record && Array.isArray(record.data)) {
        return record.data[0] ?? null;
    }

    if (record) {
        return record as ProductRecord;
    }

    return null;
};

export const flattenCategoryTree = (categories: CategoryRecord[]): CategoryRecord[] => {
    const flattened: CategoryRecord[] = [];
    const visited = new Set<number>();

    const visit = (category: CategoryRecord, level: number) => {
        if (visited.has(category.id)) {
            return;
        }

        visited.add(category.id);
        flattened.push({ ...category, level });

        if (Array.isArray(category.children)) {
            category.children.forEach((child) => visit(child, level + 1));
        }
    };

    categories.forEach((category) => visit(category, 0));

    return flattened;
};
